import React, { useEffect } from 'react';
import styled from 'styled-components';
import { SanityImage } from '@repo/ui-components/SanityImage';
import breakpoints from '@repo/ui-components/breakpoints';
import type { SanitySupportArticleDiagram } from '@repo/models/gatsby-graphql-types';

import SupportArticleText from './SupportArticleText';
import SupportArticleIconList from './SupportArticleIconList';

const DiagramContainer = styled.div<{ alignment?: string }>`
  display: flex;
  flex-direction: column;

  @media (min-width: ${breakpoints.sm}) {
    flex-direction: row;
  }

  align-items: ${props => props.alignment};
`;

const DiagramImageContainer = styled.div`
  flex-basis: 50%;
  img {
    width: 100%;
    margin-bottom: 0.25em;
  }

  @media (max-width: ${breakpoints.md}) {
    img {
      height: 300px;
      margin-bottom: 0.25em;
      max-width: 100%;
      max-height: 100%;
      object-fit: contain;
      width: 100%;
    }
  }
`;

const DiagramVideoContainer = styled.div`
  flex-basis: 50%;
  width: 100%;
  max-width: 100%;
  lite-youtube {
    border: none;
    width: 100%;
    max-width: 100%;
  }
`;

const DiagramAnnotationContainer = styled.div`
  flex-basis: 50%;
  padding-left: 0;
  margin-left: auto;
  margin-right: auto;

  @media (min-width: ${breakpoints.sm}) {
    padding-left: 1.25rem;
  }
`;

interface SupportArticleDiagramProps {
  section?: SanitySupportArticleDiagram;
}

function SupportArticleDiagram({
  section,
}: Readonly<SupportArticleDiagramProps>) {
  useEffect(() => {
    if (globalThis.window !== undefined) {
      import('lite-youtube-embed');
    }
  }, []);

  return (
    <DiagramContainer
      alignment={section.textAlignment === 'Centered' ? 'center' : 'top'}
    >
      {section?.image?.asset?._id && (
        <DiagramImageContainer>
          <SanityImage
            id={section.image.asset._id}
            hotspot={section.image?.hotspot}
            crop={section.image?.crop}
            alt={section?.image?.alt}
          />
        </DiagramImageContainer>
      )}
      {section?.video?.id && (
        <DiagramVideoContainer>
          <lite-youtube
            title={section?.video?.caption}
            videoid={section?.video?.id}
            frameBorder="0"
            allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            style={{
              backgroundImage: `url('https://i.ytimg.com/vi/${section?.video?.id}/maxresdefault.jpg')`,
            }}
            params="rel=0&listType=playlist&loop=1&showinfo=0&iv_load_policy=30&start=0&end=0"
          />
        </DiagramVideoContainer>
      )}

      {section?.annotationText?.text && (
        <DiagramAnnotationContainer>
          <SupportArticleText text={section?.annotationText?.text} />
        </DiagramAnnotationContainer>
      )}

      {section?.annotationIconList?.listItems && (
        <DiagramAnnotationContainer>
          <SupportArticleIconList section={section.annotationIconList} />
        </DiagramAnnotationContainer>
      )}
    </DiagramContainer>
  );
}

export default SupportArticleDiagram;
